import { useFlags, useOrg } from '@/stores/identity';

/**
 * Cross-org read-only pattern.
 *
 * Background: a platform admin can SELECT records (staff, locations, etc.)
 * outside their home org — RLS deliberately allows this so admins can read
 * tenant data for support / debugging. But mutation RPCs refuse to act on
 * data outside `app_current_org()`, returning 42501. Without a UI signal, an
 * admin browsing another org's record clicks Save and gets a generic Postgres
 * error — making the product look broken on the very surface support is most
 * likely to use.
 *
 * The fix is the pattern documented in
 * docs/decisions/2026-05-05_session_decisions.md §3:
 *
 *   1. Compute `useIsReadOnlyCrossOrg(entityOrgId)` — true iff the viewer is
 *      a platform admin AND the record they're looking at belongs to a
 *      different org.
 *   2. Render `<CrossOrgReadOnlyBanner />` at the top of the page when true.
 *   3. Hide / disable write surfaces (forms, save buttons, transition
 *      controls).
 *
 * Originally inlined in StaffDetail; extracted here so the rest of the app
 * can pick it up consistently. See audit F-04 for the trigger.
 */

/**
 * `true` if the current user is a platform admin viewing a record from a
 * different organization. Returns `false` for owners / managers / staff
 * regardless of org, and `false` if the entity org id isn't known yet
 * (e.g. while the underlying record is still loading).
 *
 * Pass the entity's `organization_id` (e.g. `staff.organization_id`,
 * `location.organization_id`).
 */
export function useIsReadOnlyCrossOrg(
  entityOrgId: string | null | undefined
): boolean {
  const myOrg = useOrg();
  const myFlags = useFlags();
  return (
    !!myOrg &&
    !!entityOrgId &&
    entityOrgId !== myOrg.id &&
    !!myFlags?.is_platform_admin
  );
}

interface CrossOrgReadOnlyBannerProps {
  /**
   * The kind of record being viewed, in lowercase prose. Used to fill in
   * "This {entityLabel} belongs to a different organization." Examples:
   * "staff record", "location", "set of hours", "schedule".
   */
  entityLabel: string;
  /**
   * Optional: a comma-separated list of the actions that are blocked, in
   * lowercase prose. Defaults to a generic phrase. Examples: "invites,
   * promotions, and edits", "edits and lifecycle changes".
   */
  blockedActionsLabel?: string;
}

/**
 * Amber banner shown at the top of a detail page when the viewer is a
 * platform admin browsing a record from a different organization. The
 * caller should also hide or disable the write surfaces on the page so
 * the user can't trigger an action that will fail server-side anyway.
 */
export function CrossOrgReadOnlyBanner({
  entityLabel,
  blockedActionsLabel = 'edits',
}: CrossOrgReadOnlyBannerProps) {
  return (
    <div
      role="status"
      className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <strong>Read-only: cross-org admin view.</strong> This {entityLabel}{' '}
      belongs to a different organization. You can read it because you are a
      platform admin, but {blockedActionsLabel} are blocked here. Sign in as
      that organization&apos;s owner to make changes.
    </div>
  );
}
